import type { Dispatch, RefObject, SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Heart, MessageCircle, FilterX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { optimizeCloudinaryUrl } from '@/lib/utils';
import { buildGridColumns, type MediaItem, type SortBy } from '../utils';
import { useAlbumInteractions } from '@/contexts/AlbumInteractionsContext';

interface AlbumGridProps {
  assets: MediaItem[];
  currentAssets: MediaItem[];
  filteredAndSortedAssets: MediaItem[];
  startIndex: number;
  openSlideshow: (assetIndex: number) => void;
  loadMoreRef: RefObject<HTMLDivElement | null>;
  setSelectedUsers: Dispatch<SetStateAction<Set<string>>>;
  setHideMyPhotos: Dispatch<SetStateAction<boolean>>;
  setSortBy: (sortBy: SortBy) => void;
  userVotes: Set<string>;
  userCommentedMediaIds: Set<string>;
  isLikesEnabled: boolean;
  isCommentsEnabled: boolean;
  getSetting: (key: string, fallback?: string) => string;
  getAlbumPath: (path: string) => string;
  t: (key: string) => string;
}

const AlbumGrid = ({
  assets,
  currentAssets,
  filteredAndSortedAssets,
  startIndex,
  openSlideshow,
  loadMoreRef,
  setSelectedUsers,
  setHideMyPhotos,
  setSortBy,
  userVotes,
  userCommentedMediaIds,
  isLikesEnabled,
  isCommentsEnabled,
  getSetting,
  getAlbumPath,
  t,
}: AlbumGridProps) => {
  const { getVotesCount, getCommentsCount, hasCurrentUserVoted, hasCurrentUserCommented } = useAlbumInteractions();

  const columnsCount = window.innerWidth >= 768 ? 3 : 2;
  const columns = buildGridColumns(currentAssets.length, columnsCount);

  return (
    <>
      <div className='flex gap-1 md:gap-2'>
        {columns.map((col, colIdx) => (
          <div key={colIdx} className='flex-1 space-y-1 md:space-y-2'>
            {col.map((assetIndex) => {
              const asset = currentAssets[assetIndex];
              const aspectRatio = asset.width && asset.height ? asset.width / asset.height : 1;
              const isVotedByCurrentUser = hasCurrentUserVoted(asset.id, userVotes.has(asset.id));
              const isCommentedByCurrentUser = hasCurrentUserCommented(asset.id, userCommentedMediaIds.has(asset.id));
              const votesCount = getVotesCount(asset.id, asset.votes_count ?? 0);
              const commentsCount = getCommentsCount(asset.id, asset.comments_count ?? 0);

              const displayUrl = asset.cdn_display_s || null;
              const fallbackCloud = !displayUrl
                ? optimizeCloudinaryUrl({ originalUrl: asset.secure_url, width: 600 })
                : null;

              return (
                <div
                  key={asset.id}
                  id={`thumb-${asset.id}`}
                  className='bg-muted rounded-lg overflow-hidden relative group cursor-pointer animate-fade-in'
                  style={{ aspectRatio: aspectRatio, animationDuration: '200ms' }}
                  onClick={() => openSlideshow(assetIndex)}
                >
                  <img
                    src={displayUrl || fallbackCloud || ''}
                    alt={`Foto ${startIndex + assetIndex + 1}`}
                    className='relative w-full h-full object-cover pointer-events-none'
                    loading='lazy'
                    onError={(e) => {
                      if (displayUrl && fallbackCloud) {
                        (e.currentTarget as HTMLImageElement).src = fallbackCloud;
                      }
                    }}
                  />

                  {(isLikesEnabled && votesCount > 0) || (isCommentsEnabled && commentsCount > 0) ? (
                    <>
                      <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
                      <div className="absolute bottom-2 left-2 flex items-center gap-4 pointer-events-none z-20">
                        {isLikesEnabled && votesCount > 0 && (
                          <div className="flex items-center gap-2">
                            <Heart className={`w-6 h-6 ${isVotedByCurrentUser ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                            <span className="text-white text-sm font-semibold">{votesCount}</span>
                          </div>
                        )}
                        {isCommentsEnabled && commentsCount > 0 && (
                          <div className="flex items-center gap-2">
                            <MessageCircle className={`w-6 h-6 text-white ${isCommentedByCurrentUser ? 'fill-white' : ''}`} strokeWidth="2.5" />
                            <span className="text-white text-sm font-semibold">{commentsCount}</span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {currentAssets.length < filteredAndSortedAssets.length && (
        <div ref={loadMoreRef} className="mt-6 h-8 w-full flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
        </div>
      )}

      {filteredAndSortedAssets.length === 0 && assets.length > 0 && (
        <div className='text-center py-16'>
          <div className='w-16 h-16 bg-muted rounded-full mx-auto mb-4 flex items-center justify-center'>
            <FilterX className='w-8 h-8 text-muted-foreground' />
          </div>
          <h3 className='text-lg font-medium text-foreground mb-2'>{t('noPhotosWithFilters')}</h3>
          <p className='text-muted-foreground mb-6'>
            {t('tryChangingFilters')}
          </p>
          <Button
            onClick={() => {
              setSelectedUsers(new Set());
              setHideMyPhotos(false);
              setSortBy('recent');
            }}
            variant="outline"
          >
            {t('clear')}
          </Button>
        </div>
      )}

      {assets.length === 0 && (
        <div className='text-center py-16'>
          <div className='w-16 h-16 bg-muted-foreground rounded-full mx-auto mb-4 flex items-center justify-center'>
            <svg
              className='w-8 h-8 text-background'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
              />
            </svg>
          </div>
          <h3 className='text-lg font-medium text-foreground mb-2'>{t('noPhotosUploaded')}</h3>
          <p className='text-muted-foreground mb-6'>
            {getSetting('enable_uploads') === 'true' ? t('photosWillAppearHere') : t('uploadsComingSoon')}
          </p>
          {getSetting('enable_uploads') === 'true' && (
            <Link to={getAlbumPath('/subir')}>
              <Button className='bg-primary hover:bg-secondary text-primary-foreground'>
                {t('uploadFirstPhoto')}
              </Button>
            </Link>
          )}
        </div>
      )}
    </>
  );
};

export default AlbumGrid;
